import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import RealEstateBrokerInvestmentsAPI from 'dashboard/api/realEstateBrokerInvestments';

const PAGE_SIZE = 20;
const TEMPERATURE_STORAGE_KEY = 'real_estate_broker_investment_temperature';
const EMPTY_SUMMARY = {
  vgv: {},
  quantidade_total: 0,
  diferenca_vgv: null,
  temperaturas: [],
};
const EMPTY_META = { count: 0, page: 1, per_page: PAGE_SIZE, total_pages: 0 };

export function useRealEstateBrokerInvestments({
  notify,
  t = key => key,
} = {}) {
  const temperature = ref(
    window.sessionStorage.getItem(TEMPERATURE_STORAGE_KEY) || ''
  );
  const search = ref('');
  const sort = ref('updated_desc');
  const page = ref(1);
  const rows = ref([]);
  const summary = ref({ ...EMPTY_SUMMARY });
  const meta = ref({ ...EMPTY_META });
  const loading = ref(false);
  const error = ref('');
  const lastUpdatedAt = ref(null);
  const currentTime = ref(Date.now());
  let refreshTimer;

  const totalPages = computed(() => meta.value.total_pages || 0);
  const minutesSinceRefresh = computed(() => {
    if (!lastUpdatedAt.value) return null;

    return Math.floor(
      (currentTime.value - lastUpdatedAt.value.getTime()) / 60000
    );
  });
  const requestParams = () => ({
    temperature: temperature.value || undefined,
    search: search.value.trim() || undefined,
    sort: sort.value,
    page: page.value,
    per_page: PAGE_SIZE,
  });

  const load = async () => {
    loading.value = true;
    error.value = '';
    try {
      const response =
        await RealEstateBrokerInvestmentsAPI.index(requestParams());
      rows.value = response.data?.data || [];
      summary.value = response.data?.summary || { ...EMPTY_SUMMARY };
      meta.value = response.data?.meta || { ...EMPTY_META };
      lastUpdatedAt.value = new Date();
    } catch {
      rows.value = [];
      error.value = t('REAL_ESTATE_BROKER_INVESTMENT.ERRORS.LOAD');
      if (notify) notify(error.value, null, { variant: 'error' });
    } finally {
      loading.value = false;
    }
  };

  const selectTemperature = value => {
    temperature.value = value;
    page.value = 1;
    window.sessionStorage.setItem(TEMPERATURE_STORAGE_KEY, value);
  };
  const changePage = value => {
    page.value = Math.max(1, Math.min(value, totalPages.value || 1));
  };

  watch([temperature, search, sort, page], load);
  onMounted(() => {
    refreshTimer = window.setInterval(() => {
      currentTime.value = Date.now();
    }, 60000);
    load();
  });
  onBeforeUnmount(() => window.clearInterval(refreshTimer));

  return {
    temperature,
    search,
    sort,
    page,
    rows,
    summary,
    loading,
    error,
    totalPages,
    minutesSinceRefresh,
    load,
    selectTemperature,
    changePage,
  };
}
